/*
 * Copyright (C) 2018-2019 Nettoken Ltd. All rights reserved.
 *
 * This document is the property of Nettoken Ltd.
 * It is considered confidential and proprietary.
 *
 * This document may not be reproduced or transmitted in any form,
 * in whole or in part, without the express written permission of
 * Nettoken Ltd.
 */
import {
  __CREDENTIAL_SKIP_CRYPTO_ON_KEYS__,
  __MOCK_PK__,
  __MOCK_SK__,
} from './const';

const ERR_FORMAT = 'Invalid format(s) provided!';
const ERR_KEYPAIR = 'Invalid keypair provided!';

let b64variant;
let externalKeyPair;
let masterKeyPair;
let sessionKeyPair;
let sodium;

/**
 * Convert bytes variable to the requested format.
 *
 * @param {string|UInt8Array} input
 * @param {string} [inputFormat='bytes'] Input format. ('base64'|'bytes'|'hex')
 * @param {string} [outputFormat='plain'] Output format. ('base64'|'bytes'|'hex'|'plain')
 *
 * @return {string|UInt8Array}
 */
const convertInput = (input, inputFormat = 'bytes', outputFormat = 'plain') => {
  if (inputFormat === outputFormat) return input;

  let bytes;
  switch (inputFormat) {
    case 'base64':
      bytes = sodium.from_base64(input, b64variant);
      break;

    case 'hex':
      bytes = sodium.from_hex(input);
      break;

    case 'plain':
      throw new Error(`Input type "${inputFormat}" not supported`);

    case 'bytes':
    default:
      bytes = input;
      break;
  }

  if (outputFormat === 'bytes') return bytes;
  if (outputFormat === 'hex') return sodium.to_hex(bytes);
  const base64 = sodium.to_base64(bytes, b64variant);
  if (outputFormat === 'base64') return base64;
  const plaintext = atob(base64);
  return decodeURIComponent(escape(plaintext));
};

/**
 * Decrypt a message.
 *
 * @param {string} encrypted Base64 encoded message.
 * @param {string} format Response format. ('base64'|'bytes'|'plain')
 * @param {object} keypair Instance of KeyPair class.
 *
 * @returns {*}
 */
const decrypt = (encrypted, format, keypair) => {
  if (!keypair.defined()) throw new Error(ERR_KEYPAIR);

  const canParse = parsable(encrypted);
  if (!canParse) return encrypted;

  if (!format) throw new Error(ERR_FORMAT);

  const ciphertext = sodium.from_base64(encrypted, b64variant);

  const decrypted = sodium.crypto_box_seal_open(
    ciphertext,
    keypair.publicKey,
    keypair.secretKey
  );
  return convertInput(decrypted, 'bytes', format);
};

/**
 * Encrypt a message.
 *
 * @param {string} message String with the message to encrypt.
 * @param {string} inputFormat Message parameter format. ('base64'|'plain')
 * @param {string} outputFormat Response format. ('base64'|'bytes'|'plain')
 * @param {object} keypair Instance of KeyPair class.
 *
 * @returns {*}
 */
const encrypt = (message, inputFormat, outputFormat, keypair) => {
  if (!keypair.defined()) throw new Error(ERR_KEYPAIR);

  const canParse = parsable(message);
  if (!canParse) return message;

  if (!inputFormat || !outputFormat) throw new Error(ERR_FORMAT);

  let base64 = message;
  if (inputFormat === 'plain') {
    base64 = btoa(unescape(encodeURIComponent(message)));
  }

  const messageBytes = sodium.from_base64(base64, b64variant);
  const ciphertext = sodium.crypto_box_seal(messageBytes, keypair.publicKey);
  return convertInput(ciphertext, 'bytes', outputFormat);
};

/**
 * Loop a credential object and perform a cryptographic operation on its keys.
 *
 * @param {object} credential Credential object adhering to our schema.
 * @param {function} crypto Function to execute on every iteration.
 * @param {array} [ignore=__CREDENTIAL_SKIP_CRYPTO_ON_KEYS__] Skip operations on these attributes.
 *
 * @returns {object}
 */
const loopCredential = (credential, crypto, skip = __CREDENTIAL_SKIP_CRYPTO_ON_KEYS__) => {
  const keys = Object.keys(credential);
  const result = {};
  for (let i = 0; i < keys.length; i += 1) {
    const key = keys[i];
    const value = credential[key];
    const skipped = skip.includes(key);

    try {
      result[key] = skipped ? value : crypto(value);
    }
    catch (e) {
      console.log('Problem with shared account: ', credential, {
        e,
        key,
        value,
        skipped,
      });
      // return null;
    }
  }
  return result;
};

/**
 * Check if the passed value is parsable by cryptographic functions.
 * Only truthy strings are accepted.
 *
 * @param {any} value
 *
 * @returns {boolean}
 */
const parsable = value => value && typeof value === 'string';

/**
 * @param {*} encrypted
 * @param {string} [format='plain']
 *
 * @returns {*}
 */
const decryptUsingMasterKeyPair = (encrypted, format = 'plain') => decrypt(encrypted, format, masterKeyPair);

/**
 * @param {*} message
 * @param {string} [inputFormat='plain']
 * @param {string} [outputFormat='base64']
 *
 * @returns {*}
 */
const encryptUsingMasterKeyPair = (message, inputFormat = 'plain', outputFormat = 'base64') => encrypt(message, inputFormat, outputFormat, masterKeyPair);

/**
 * @param {*} encrypted
 * @param {string} [format='plain']
 *
 * @returns {*}
 */
const decryptUsingSessionKeyPair = (encrypted, format = 'plain') => decrypt(encrypted, format, sessionKeyPair);

/**
 * @param {*} message
 * @param {string} [inputFormat='base64']
 * @param {string} [outputFormat='base64']
 *
 * @returns {*}
 */
const encryptUsingSessionKeyPair = (message, inputFormat = 'base64', outputFormat = 'base64') => encrypt(message, inputFormat, outputFormat, sessionKeyPair);

/**
 * @param {*} encrypted
 * @param {string} [format='plain']
 * @param {object} [keypair=externalKeyPair]
 *
 * @returns {*}
 */
const decryptUsingExternalKeyPair = (encrypted, format = 'plain', keypair = externalKeyPair) => decrypt(encrypted, format, keypair);

/**
 * @param {*} message
 * @param {object} keypair
 * @param {string} [inputFormat='base64']
 * @param {string} [outputFormat='base64']
 *
 * @returns {*}
 */
const encryptUsingExternalKeyPair = (message, keypair, inputFormat = 'base64', outputFormat = 'base64') => encrypt(message, inputFormat, outputFormat, keypair);

/**
 * @param {object} credential
 *
 * @returns {object}
 */
const decryptCredential = credential => loopCredential(credential, decryptUsingMasterKeyPair);

const decryptSharedCredential = (credential, keypair) => {
  const crypto = decryptUsingSharedCredential(keypair);
  return loopCredential(credential, crypto);
}

const decryptUsingSharedCredential = (keypair) => (encrypted, format = 'plain') => decrypt(encrypted, format, keypair);

const loopDashboard = (dashboard, crypto) => {
  const keys = Object.keys(dashboard);
  const result = {};
  for (let i = 0; i < keys.length; i += 1) {
    const key = keys[i];
    const value = dashboard[key];
    try {
      result[key] = value;
    }
    catch (e) {
      console.log('Problem with shared dashboard: ', dashboard);
    }
  }
  result['name'] = crypto(dashboard.name);
  return result;
};

const decryptDashboard = dashboard => loopDashboard(dashboard, decryptUsingMasterKeyPair);

const decryptSharedDashboard = (dashboard, keypair) => {
  const crypto = decryptUsingSharedDashboard(keypair);
  return loopDashboard(dashboard, crypto);
}

const decryptUsingSharedDashboard = (keypair) => (encrypted, format = 'plain') => decrypt(encrypted, format, keypair);
/**
 * @param {object} credential
 *
 * @returns {object}
 */
const encryptCredential = credential => loopCredential(credential, encryptUsingMasterKeyPair);

const encryptSharedCredential = (credential, keypair) => {
  const crypto = encryptUsingSharedCredential(keypair);
  return loopCredential(credential, crypto);
}

const encryptUsingSharedCredential = (keypair) => (encrypted, inputFormat = 'plain', outputFormat = 'base64') => encrypt(encrypted, inputFormat, outputFormat, keypair);

/**
 * Return a new key pair.
 *
 * @param {boolean} mock Return always the same result?
 * @param {string} [format='bytes']
 *
 * @returns {object}
 * @property {*} publicKey
 * @property {*} secretKey
 */
const generateKeyPair = (mock, format = 'bytes') => {
  let { publicKey, privateKey: secretKey } = sodium.crypto_box_keypair();
  if (mock) {
    publicKey = sodium.from_base64(__MOCK_PK__, b64variant);
    secretKey = sodium.from_base64(__MOCK_SK__, b64variant);
  }
  return {
    publicKey: convertInput(publicKey, 'bytes', format),
    secretKey: convertInput(secretKey, 'bytes', format),
  };
};

/**
 * Initialise the session key pair. Calling this multiple times causes
 * the application to overwrite the previous key pair.
 *
 * @param {boolean} mock Use mock keypair?
 */
const setSessionKeyPair = mock => {
  const { publicKey, secretKey } = generateKeyPair(mock);
  sessionKeyPair.set(publicKey, secretKey);
};

const exported = {
  convertInput,
  decryptCredential,
  decryptSharedCredential,
  decryptDashboard,
  decryptSharedDashboard,  
  decryptUsingExternalKeyPair,
  decryptUsingMasterKeyPair,
  decryptUsingSessionKeyPair,
  encryptCredential,
  encryptSharedCredential,
  encryptUsingExternalKeyPair,
  encryptUsingSessionKeyPair,
  generateKeyPair,
  setSessionKeyPair,
  encryptUsingMasterKeyPair,
};

export default (sod, b64v, masterKeys, sessionKeys, externalKeys) => {
  sodium = sod;
  b64variant = b64v;
  externalKeyPair = externalKeys;
  masterKeyPair = masterKeys;
  sessionKeyPair = sessionKeys;
  return exported;
};
